import type { UniversalIssueWithScreenshot } from '@deque/axe-devtools-issues-utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, TooltipTabstop } from '@deque/cauldron-react';

import type {
  ProductSlugs,
  SupportedIntegrationProductSlugs
} from '../../../common/constants';
import { getIntegrationProductInfoFromSlug } from '../../utils/get-integration-product-name-from-slug';
import ShareToIntegrationModalV1 from './ShareToIntegrationModalV1';
import { useIntegrations } from '../../../app/hooks/useIntegrations';
import Link from '../Link';
import styles from './ShareToIntegrationButton.css';
import { useFeatureFlagState } from '../../contexts/featureFlags';
import ShareToIntegrationModal from './ShareToIntegrationModal/ShareToIntegrationModal';
import { type IntegrationConnection } from '../../utils/integrations-client/jira/connections';

export interface ShareToIntegrationButtonProps {
  integrationProductSlug: SupportedIntegrationProductSlugs;
  issues: UniversalIssueWithScreenshot[];
  testId: string;
  issuesProductSourceSlug: ProductSlugs;
  onIntegrationShare?: () => void;
  multiple?: boolean;
  connections?: IntegrationConnection[] | null;
}

export const ISSUE_SENT = 'issueSent';

const ShareToIntegrationButton = ({
  integrationProductSlug,
  issues,
  testId,
  issuesProductSourceSlug,
  onIntegrationShare,
  multiple = false,
  connections = []
}: ShareToIntegrationButtonProps) => {
  const { t } = useTranslation();
  const hasJiraEnumSupport = useFeatureFlagState('jira-enum-support-v1');

  const { enabledIntegrationProductSlugs, canAccessIntegrationConfiguration } =
    useIntegrations();

  const [showShareModal, setShowShareModal] = useState(false);

  const integrationName = getIntegrationProductInfoFromSlug(
    integrationProductSlug as unknown as ProductSlugs
  ).displayName;

  if (
    enabledIntegrationProductSlugs.length === 0 ||
    !canAccessIntegrationConfiguration ||
    !onIntegrationShare
  ) {
    return null;
  }

  if (
    issues.every(
      issue =>
        issue.integrationUrl &&
        issue.integrationUrl === issues[0].integrationUrl
    )
  ) {
    return (
      <Link
        thin
        variant="button-secondary"
        // There should always be a link because of the check above
        url={issues[0].integrationUrl ?? ''}
        className={styles.shareButton}
      >
        {t(`OPEN IN {{ integration }}`, {
          integration: integrationName.toLocaleUpperCase()
        })}
      </Link>
    );
  }

  if (multiple && issues.some(issue => issue.integrationUrl)) {
    return (
      <TooltipTabstop
        tooltip={t(
          `1 or more of the issues below have already been shared to {{ integration }}`,
          {
            integration: integrationName
          }
        )}
      >
        <Button
          thin
          variant="secondary"
          disabled
          className={styles.shareButton}
        >
          <Icon type="share-nodes" />
          {t('SHARE ALL TO {{ integration }}', {
            integration: integrationName.toLocaleUpperCase()
          })}
        </Button>
      </TooltipTabstop>
    );
  }

  const connectedConnections =
    connections?.filter(
      ({ status, product_slug }) =>
        status === 'connected' && product_slug === integrationProductSlug
    ) || [];

  return (
    <>
      <Button
        thin
        variant="secondary"
        onClick={() => setShowShareModal(true)}
        className={styles.shareButton}
      >
        <Icon type="share-nodes" />
        {t('SHARE {{ multipleText }}TO {{ integration }}', {
          multipleText: multiple ? t('ALL ') : '',
          integration: integrationName.toLocaleUpperCase()
        })}
      </Button>
      {showShareModal &&
        (hasJiraEnumSupport ? (
          <ShareToIntegrationModal
            connections={connectedConnections}
            integrationProductSlug={integrationProductSlug}
            setShow={setShowShareModal}
            multiple={multiple}
            issues={issues}
            testId={testId}
            issuesProductSourceSlug={issuesProductSourceSlug}
            onIntegrationShare={onIntegrationShare}
          />
        ) : (
          <ShareToIntegrationModalV1
            integrationProductSlug={integrationProductSlug}
            setShow={setShowShareModal}
            multiple={multiple}
            issues={issues}
            testId={testId}
            issuesProductSourceSlug={issuesProductSourceSlug}
            onIntegrationShare={onIntegrationShare}
          />
        ))}
    </>
  );
};

export default ShareToIntegrationButton;
